import {
  Goal,
  GoalFilterDocument,
  GoalSortingMode,
  ID,
  OrderDirection,
} from '@griply/types';
import { getGoalListOrder, getSubGoalsGoalFilterDocument } from '@griply/utils';
import {
  useAddSubGoal,
  useSortedGoals,
  useSubGoals,
  useUpdateGoal,
} from 'features/goals';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback, useMemo, useRef } from 'react';
import { useUser } from 'shared/hooks/use-user';
import { GoalFilterList, SortBar } from 'shared/views/goal-filter-list';

import { useGoalTreeReorder } from '../../hooks/use-goal-tree-reorder';
import { SubGoalsEmptyState } from '../sub-goals-empty-state';
import * as Styled from './subgoals-overview.style';

type SubgoalsOverviewProps = {
  goal: Goal;
  scrollElement?: React.RefObject<HTMLDivElement>;
  hideCompleted?: boolean;
  hideArchived?: boolean;
  fullSize?: boolean;
};

export const SubgoalsOverview: React.FC<SubgoalsOverviewProps> = ({
  goal,
  scrollElement,
  hideCompleted,
  hideArchived,
  fullSize,
}) => {
  const { submit: updateGoal } = useUpdateGoal();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const subgoals = useSubGoals(goal.id, true);
  const allGoals = useSortedGoals();
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();
  const { onAddSubGoal } = useAddSubGoal(goal);

  // The SortBar is the goal table's column header (each column label doubles as
  // a quick-sort toggle); it writes the same per-goal `goalSortingMode` as the
  // SubgoalsFilterButton, so the two stay in sync.
  const sortMode = goal.goalSortingMode ?? GoalSortingMode.Custom;
  const sortDirection = user?.settings?.goalSortDirection ?? OrderDirection.Asc;

  const onSortChange = useCallback(
    (mode: GoalSortingMode) => {
      updateGoal({ id: goal.id, goalSortingMode: mode });
      updateUserSettings({ goalSortDirection: OrderDirection.Asc });
    },
    [goal.id, updateGoal, updateUserSettings],
  );

  const onSortDirectionToggle = useCallback(() => {
    updateUserSettings({
      goalSortDirection:
        sortDirection === OrderDirection.Asc
          ? OrderDirection.Desc
          : OrderDirection.Asc,
    });
  }, [sortDirection, updateUserSettings]);

  const onSectionCollapse = useCallback(
    (newCollapsedSections: ID[]) =>
      updateGoal({
        id: goal.id,
        collapsedSubGoals: newCollapsedSections,
      }),
    [goal.id, updateGoal],
  );

  const filterDocument = useMemo<GoalFilterDocument>(
    () =>
      getSubGoalsGoalFilterDocument(
        goal,
        subgoals.map(({ id }) => id),
      ),
    [goal, subgoals],
  );

  const customOrder = useMemo(
    () =>
      getGoalListOrder({
        goals: subgoals,
        allGoals,
        sortMode,
        sortDirection,
        goalSorting: goal.goalSorting,
      }),
    [subgoals, allGoals, sortMode, sortDirection, goal.goalSorting],
  );

  const onReorder = useGoalTreeReorder(goal);

  const onHideRoadmap = () =>
    updateGoal({
      id: goal.id,
      showRoadmap: false,
    });

  if (!subgoals.length) {
    return <SubGoalsEmptyState onAdd={onAddSubGoal} onHide={onHideRoadmap} />;
  }

  return (
    <Styled.ScrollContainer ref={scrollContainerRef} $scrollAuto={!!fullSize}>
      <SortBar
        value={sortMode}
        direction={sortDirection}
        onChange={onSortChange}
        onDirectionToggle={onSortDirectionToggle}
        hideLifeArea
      />
      <GoalFilterList
        filterDocument={filterDocument}
        customOrder={customOrder}
        onSectionCollapse={onSectionCollapse}
        scrollElement={
          fullSize ? scrollContainerRef : scrollElement ?? scrollContainerRef
        }
        onReorder={onReorder}
        hideCompleted={hideCompleted}
        hideArchived={hideArchived}
        hideLifeArea
      />
    </Styled.ScrollContainer>
  );
};
